import { AfterViewInit, Component, DoCheck, ElementRef, HostBinding, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CustomerService } from '../../services/customer.service';
import { MODAL, ModalService } from '../../services/modal.service';
import { environment } from '../../../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { CurrentService } from '../../services/current.service';
import { DatasService } from '../../services/datas.service';
import { HbmIframeService } from '../../services/hbm/hbm-iframe.service';
import { faArrowRight, faAt, faCalculator, faDoorOpen, faHome, faInfoCircle, faStore, faUser } from '@fortawesome/free-solid-svg-icons';
import { GTM } from 'src/app/services/gtm.service';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { HeaderActionsService } from './header-actions.service';
import { CommunicationsService } from '../../services/communications/communications.service';
import { CommunicationLevel, HeadOfficeInfo } from '../../services/communications/models';
import { filter } from 'rxjs';
import { formatParisDate, formatParisDay } from '../../services/communications/utils/communication-date';

interface CommunicationPanelGroup {
  date: string;
  items: HeadOfficeInfo[];
}

@Component({
  selector: 'ng-casto-header',
  templateUrl: './header.component.html',
})
export class HeaderComponent extends Subscribable implements OnInit, AfterViewInit, OnDestroy, DoCheck {

  public vendorContext: boolean;

  protected displayProjectInfo: boolean = false;

  protected readonly faCalculator = faCalculator;
  protected readonly faArrowRight = faArrowRight;
  protected readonly faDoorOpen = faDoorOpen;
  protected readonly faUser = faUser;
  protected readonly faStore = faStore;
  protected readonly faAt = faAt;
  protected readonly faHome = faHome;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly CommunicationLevel = CommunicationLevel;
  protected readonly formatParisDate = formatParisDate;
  protected visibleCommunications: HeadOfficeInfo[] = [];
  protected unreadInfoCount = 0;
  protected communicationsPanelOpen = false;
  protected communicationsPanelAlertsOnly = false;
  protected communicationsPanelLevel: CommunicationLevel | null = null;
  protected alertDialog: HeadOfficeInfo | null = null;
  private communicationsStarted = false;
  private openingAlertId: string | null = null;

  @ViewChild('alertBanner') private alertBanner?: ElementRef<HTMLElement>;

  private headerResizeObserver?: ResizeObserver;
  private headerExtraHeight = -1;

  @HostBinding('class.configuration')
  get isInConfiguration(): boolean {
    return this.router.url.indexOf('/configuration') > 0;
  }

  @HostBinding('class.has-header-actions')
  get hasHeaderActions(): boolean {
    return this.headerActions.template() !== null;
  }

  @HostBinding('class.has-communications-overlay')
  get hasCommunicationsOverlay(): boolean {
    return this.alertDialog !== null;
  }

  get isOnHomePage(): boolean {
    return this.router.url === '/';
  }

  get bannerAlerts(): HeadOfficeInfo[] {
    return this.visibleCommunications
      .filter(item => item.level !== CommunicationLevel.Information)
      .sort((first, second) =>
        second.level - first.level || Date.parse(second.publicationUtc) - Date.parse(first.publicationUtc)
      );
  }

  get bannerAlert(): HeadOfficeInfo | null {
    return this.bannerAlerts[0] ?? null;
  }

  get alertDialogIndex(): number {
    if (!this.alertDialog) return -1;
    return this.bannerAlerts.findIndex(item => item.id === this.alertDialog?.id);
  }

  get alertDialogPosition(): number {
    return this.alertDialogIndex + 1;
  }

  get canNavigateAlertDialog(): boolean {
    return this.alertDialogIndex >= 0 && this.bannerAlerts.length > 1;
  }

  get communicationsPanelItems(): HeadOfficeInfo[] {
    const items = this.communicationsPanelAlertsOnly ? this.bannerAlerts : this.visibleCommunications;
    return items
      .filter(item => this.communicationsPanelLevel === null || item.level === this.communicationsPanelLevel)
      .sort((first, second) => Date.parse(second.publicationUtc) - Date.parse(first.publicationUtc));
  }

  get communicationsPanelLevelFilters(): Array<CommunicationLevel | null> {
    return this.communicationsPanelAlertsOnly
      ? [null, CommunicationLevel.ModerateAlert, CommunicationLevel.StrongAlert]
      : [null, CommunicationLevel.Information, CommunicationLevel.ModerateAlert, CommunicationLevel.StrongAlert];
  }

  get communicationsPanelGroups(): CommunicationPanelGroup[] {
    return this.communicationsPanelItems.reduce<CommunicationPanelGroup[]>((groups, item) => {
      const date = formatParisDay(item.publicationUtc);
      const currentGroup = groups[groups.length - 1];
      if (currentGroup?.date === date) currentGroup.items.push(item);
      else groups.push({ date, items: [item] });
      return groups;
    }, []);
  }

  /** renvoie le lien vers la prise de rendez-vous en magasin */
  get storeAppointmentLink(): string {
    if (!this.customer?.context || !this.isInConfiguration) {
      return null;
    }

    return environment.byMe[this.customer?.context]?.storeAppointmentLink;
  }

  /** renvoie le lien vers la prise de rendez-vous en visio */
  get onlineAppointmentLink(): string {
    if (!this.customer?.context || !this.isInConfiguration) {
      return null;
    }

    return environment.byMe[this.customer?.context]?.onlineAppointmentLink;
  }

  get vendorFirstName() {
    return this.current.vendor?.givenName;
  }

  get customerIsLogged() {
    return this.customer?.userDatas;
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    public customer: CustomerService,
    public datas: DatasService,
    public current: CurrentService,
    public byMe: HbmIframeService,
    private readonly gtm: GTM,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly communications: CommunicationsService,
    protected readonly headerActions: HeaderActionsService
  ) {
    super();
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        this.displayProjectInfo = currentPricing !== undefined;
      }),
      this.communications.visibleInfos$.subscribe(infos => {
        this.visibleCommunications = infos;
        this.unreadInfoCount = infos.filter(info => !info.isRead).length;
        this.openRequiredAlert(false);
      }),
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.openRequiredAlert(true))
    );
  }

  ngOnInit(): void {
    this.vendorContext = environment.vendor;
    super.subscribe();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    // Hors zone Angular : la mesure ne touche que le DOM, inutile de declencher un
    // cycle de detection de changements a chaque redimensionnement du header.
    this.zone.runOutsideAngular(() => {
      this.headerResizeObserver = new ResizeObserver(() => this.publishHeaderExtraHeight());
      this.headerResizeObserver.observe(this.elementRef.nativeElement);
      this.publishHeaderExtraHeight();
    });
  }

  ngOnDestroy(): void {
    this.headerResizeObserver?.disconnect();
    this.headerResizeObserver = undefined;
    this.resetHeaderExtraHeight();
    if (this.communicationsStarted) this.communications.stop();
    super.unsubscribe();
  }

  /**
   * Publie la hauteur ajoutee au header par le bandeau d'alerte (0 sans bandeau) afin
   * que les pages decalent leur contenu sous le header reel plutot que sous une
   * hauteur figee, sans deborder de la fenetre.
   */
  private publishHeaderExtraHeight(): void {
    const extraHeight = Math.round(this.measureAlertBannerHeight());
    if (extraHeight === this.headerExtraHeight) return;

    this.headerExtraHeight = extraHeight;
    document.documentElement.style.setProperty('--casto-header-extra-height', `${extraHeight}px`);
  }

  /** Hauteur occupee dans le header par le bandeau d'alerte, marges comprises. */
  private measureAlertBannerHeight(): number {
    const banner = this.alertBanner?.nativeElement;
    if (!banner) return 0;

    const styles = window.getComputedStyle(banner);
    const marginTop = parseFloat(styles.marginTop) || 0;
    const marginBottom = parseFloat(styles.marginBottom) || 0;
    return Math.max(0, banner.getBoundingClientRect().height + marginTop + marginBottom);
  }

  private resetHeaderExtraHeight(): void {
    this.headerExtraHeight = -1;
    document.documentElement.style.removeProperty('--casto-header-extra-height');
  }

  ngDoCheck(): void {
    if (this.vendorContext && this.current.vendor && !this.communicationsStarted) {
      this.communicationsStarted = true;
      void this.communications.start().then(() => this.openRequiredAlert(true));
    } else if (this.communicationsStarted && !this.current.vendor) {
      this.communicationsStarted = false;
      this.alertDialog = null;
      this.openingAlertId = null;
      this.communications.stop();
    }
  }

  protected async openAlert(communication: HeadOfficeInfo): Promise<void> {
    this.openingAlertId = communication.id;
    try {
      const detail = await this.communications.getInfo(communication.id);
      if (this.openingAlertId === communication.id && this.current.vendor) this.alertDialog = detail;
    } catch {
      if (this.openingAlertId === communication.id && this.current.vendor) this.alertDialog = communication;
    } finally {
      if (this.openingAlertId === communication.id) this.openingAlertId = null;
    }
  }

  protected openNextBannerAlert(): void {
    if (this.bannerAlerts.length < 2) return;
    void this.openAlert(this.bannerAlerts[1]);
  }

  protected openPreviousAlert(): void {
    void this.openAdjacentAlert(-1);
  }

  protected openNextAlert(): void {
    void this.openAdjacentAlert(1);
  }

  protected openCommunicationsPanel(alertsOnly = false): void {
    this.communicationsPanelAlertsOnly = alertsOnly;
    this.communicationsPanelLevel = null;
    this.communicationsPanelOpen = true;
  }

  protected filterCommunicationsPanel(level: CommunicationLevel | null): void {
    this.communicationsPanelLevel = level;
  }

  protected unreadCommunicationsPanelCount(level: CommunicationLevel | null): number {
    const items = this.communicationsPanelAlertsOnly ? this.bannerAlerts : this.visibleCommunications;
    return items.filter(item => !item.isRead && (level === null || item.level === level)).length;
  }

  protected closeCommunicationsPanel(): void {
    this.communicationsPanelOpen = false;
  }

  protected openCommunicationFromPanel(communication: HeadOfficeInfo): void {
    this.closeCommunicationsPanel();
    void this.openAlert(communication);
  }

  protected levelLabelKey(level: CommunicationLevel): string {
    if (level === CommunicationLevel.StrongAlert) return 'communications.level.strong';
    if (level === CommunicationLevel.ModerateAlert) return 'communications.level.moderate';
    return 'communications.level.information';
  }

  protected levelShortLabelKey(level: CommunicationLevel): string {
    if (level === CommunicationLevel.StrongAlert) return 'communications.level.strongShort';
    return this.levelLabelKey(level);
  }

  protected async closeAlert(): Promise<void> {
    const communication = this.alertDialog;
    this.alertDialog = null;
    if (!communication || communication.isRead) return;
    await this.communications.markInfoRead(communication.id);
    await this.communications.refreshInfos(false);
  }

  private openAdjacentAlert(direction: -1 | 1): void {
    if (!this.canNavigateAlertDialog) return;
    const nextIndex = (this.alertDialogIndex + direction + this.bannerAlerts.length) % this.bannerAlerts.length;
    void this.openAlert(this.bannerAlerts[nextIndex]);
  }

  private openRequiredAlert(allowMedium: boolean): void {
    if (this.alertDialog || this.openingAlertId) return;
    const high = this.bannerAlerts.find(item => item.level === CommunicationLevel.StrongAlert && !item.isRead);
    if (high) {
      void this.openAlert(high);
      return;
    }
    if (allowMedium && this.isOnHomePage) {
      const moderate = this.bannerAlerts.find(item => item.level === CommunicationLevel.ModerateAlert && !item.isRead);
      if (moderate) void this.openAlert(moderate);
    }
  }

  clickLogoHandler(e: Event): void {
    if (this.router.url != '/') {
      return;
    }

    e.preventDefault();
  }

  goToStoreAppointmentUrl(): void {
    this.gtm.trackPage(`${this.router.url}/rendez-vous-en-magasin`, 'RDV en magasin');
    this.modalService.display(MODAL.APPOINTMENT_LINK, {
      appointmentLink: this.storeAppointmentLink,
    });
  }

  goToOnlineAppointmentUrl(): void {
    this.gtm.trackPage(`${this.router.url}/rendez-vous-a-distance`, 'RDV à distance');
    this.modalService.display(MODAL.APPOINTMENT_LINK, {
      appointmentLink: this.onlineAppointmentLink,
    });
  }

  askLogout(): void {
    this.modalService.display(MODAL.LOGOUT);
  }

  signIn(): void {
    this.modalService.display(MODAL.LOGIN);
  }
}
